/**
 * Notification System Context
 *
 * Provides a context-based API for triggering toast notifications from
 * anywhere in the application. Uses MUI Snackbar + Alert for rendering.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { Snackbar, Alert, IconButton, Slide, type SlideProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function SlideDown(props: SlideProps) {
    return <Slide {...props} direction="down" />;
}

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';

export interface NotificationOptions {
    message: string;
    severity: NotificationSeverity;
    autoCloseDuration?: number; // ms, undefined = no auto-close
}

export interface NotificationContextValue {
    notify: (options: NotificationOptions) => void;
}

interface QueuedNotification extends NotificationOptions {
    id: number;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

let nextId = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [queue, setQueue] = useState<QueuedNotification[]>([]);
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState<QueuedNotification | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const notify = useCallback((options: NotificationOptions) => {
        const notification: QueuedNotification = { ...options, id: nextId++ };
        setQueue(prev => [...prev, notification]);
    }, []);

    // When queue changes and nothing is currently displayed, show the next one
    useEffect(() => {
        if (!current && queue.length > 0) {
            setCurrent(queue[0]);
            setQueue(prev => prev.slice(1));
            setOpen(true);
        }
    }, [queue, current]);

    // Auto-close timer
    useEffect(() => {
        if (open && current?.autoCloseDuration) {
            timerRef.current = setTimeout(() => {
                setOpen(false);
            }, current.autoCloseDuration);
            return () => {
                if (timerRef.current) clearTimeout(timerRef.current);
            };
        }
    }, [open, current]);

    const handleClose = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setOpen(false);
    }, []);

    const handleExited = useCallback(() => {
        setCurrent(null);
    }, []);

    return (
        <NotificationContext.Provider value={{ notify }}>
            {children}
            <Snackbar
                open={open}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={handleClose}
                TransitionComponent={SlideDown}
                TransitionProps={{ onExited: handleExited }}
                sx={{
                    // Offset to center over the main content area (right of sidebar)
                    left: { xs: '50%', md: 'calc(50% + 110px)' },
                }}
            >
                {current ? (
                    <Alert
                        severity={current.severity}
                        variant="filled"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={handleClose}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{
                            width: '100%',
                            color: '#fff',
                            ...({
                                info: { bgcolor: '#1976d2' },
                                success: { bgcolor: '#2e7d32' },
                                warning: { bgcolor: '#ed6c02' },
                                error: { bgcolor: '#d32f2f' },
                            }[current.severity]),
                        }}
                    >
                        {current.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </NotificationContext.Provider>
    );
}

export function useNotification(): NotificationContextValue {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
}
